import { Flex, Box, Heading, useDisclosure } from '@chakra-ui/react';
import { Formik, Form } from 'formik';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import Button from 'components/Button';
import { FormikCheckbox, FormikInput } from 'components/Formik';
import Loader from 'components/Loader';
import { ConfirmationModal } from 'components/Modal';
import { useFetchPublicApiWebhookSettings } from 'hooks/integrations/publicApi/useFetchPublicApiWebhookSettings';
import { useSubscribePublicApiWebhook } from 'hooks/integrations/publicApi/useSubscribePublicApiWebhook';
import { useUnsubscribePublicApiWebhook } from 'hooks/integrations/publicApi/useUnsubscribePublicApiWebhook';

import { initialWebhooksData, webhookFormValidationSchema } from './schema';

const Webhook = () => {
  const { t } = useTranslation(['common', 'integrations']);

  const [formData, setFormData] = useState(initialWebhooksData);
  const [eventOptions, setEventOptions] = useState<Array<ILabelValueOptions>>([]);

  const {
    isOpen: isUnsubscribeModalOpen,
    onOpen: onUnsubscribeModalOpen,
    onClose: onUnsubscribeModalClose,
  } = useDisclosure();

  const {
    data: webhookData,
    refetch,
    status,
  } = useFetchPublicApiWebhookSettings({
    onSuccess: (data: any) => {
      const events = data?.payload?.events;
      const subscription = data?.payload?.subscription;

      if (Array.isArray(events) && events.length) {
        const options = events.map(event => {
          if (event === '*') {
            return { label: 'All', value: '*' };
          } else {
            return { label: event, value: event };
          }
        });
        setEventOptions(options);
      }

      if (subscription) {
        let formEvents = subscription?.events || [];

        if (formEvents.includes('*')) {
          formEvents = events;
        }

        const formData = {
          url: subscription?.url || '',
          signature_secret_key: subscription?.signature_secret_key || '',
          active: true,
          events: formEvents,
        };

        setFormData(formData);
      } else {
        setFormData(initialWebhooksData);
      }
    },
  });

  const { mutateAsync: subscribeMutateAsync, isLoading: isSubscribeWebhookLoading } = useSubscribePublicApiWebhook();
  const { mutateAsync: unSubscribeMutateAsync, isLoading: isUnsubscribeWebhookLoading } =
    useUnsubscribePublicApiWebhook();

  const handleUnsubscribe = () => {
    unSubscribeMutateAsync().then(() => {
      onUnsubscribeModalClose();
      refetch();
    });
  };

  const handleFormSubmit = (values: typeof initialWebhooksData) => {
    const postData = {
      url: values.url,
      events: Array.isArray(values.events) && values.events.includes('*') ? ['*'] : values.events,
      signature_secret_key: values.signature_secret_key,
      active: true,
    };

    subscribeMutateAsync(postData).then(() => {
      refetch();
    });
  };

  const handleCheckboxChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void,
    values: typeof initialWebhooksData
  ) => {
    const allEvents = webhookData?.payload?.events || [];

    if (e.target.value === '*') {
      if (e.target.checked) {
        setFieldValue('events', allEvents);
      } else {
        setFieldValue('events', []);
      }
    } else {
      if (e.target.checked && eventOptions.length - 2 === values.events.length && !values.events.includes('*')) {
        setFieldValue('events', allEvents);
      }

      if (!e.target.checked && eventOptions.length - 1 !== values.events.length && values.events.includes('*')) {
        const newValues = values.events.filter((event: string) => event !== '*' && event !== e.target.value);
        setFieldValue('events', newValues);
      }
    }
  };

  return (
    <Box boxShadow="0 0 5px 0 #e1e1e1" borderRadius="5px" padding="10px 20px 20px 20px">
      <Heading size="sm" mb="4">
        {t('common:webhook')}
      </Heading>
      {status === 'loading' ? (
        <Loader height="300px" />
      ) : (
        <Formik
          initialValues={formData}
          enableReinitialize
          validationSchema={webhookFormValidationSchema}
          onSubmit={handleFormSubmit}
        >
          {({ setFieldValue, values }) => (
            <Form noValidate>
              <FormikInput name="url" label="URL" formControlProps={{ isRequired: true }} />
              <FormikInput
                name="signature_secret_key"
                label="Secret Key"
                type="password"
                formControlProps={{ isRequired: true }}
              />
              <FormikCheckbox
                name="events"
                label="Events"
                options={eventOptions}
                onChangeCallback={e => handleCheckboxChange(e, setFieldValue, values)}
              />
              <Flex alignItems="center" justifyContent="space-between">
                <Button type="submit" colorScheme="secondary" minWidth="100px" isLoading={isSubscribeWebhookLoading}>
                  {t('common:subscribe')}
                </Button>
                {webhookData?.payload?.subscription && (
                  <Button colorScheme="red" onClick={onUnsubscribeModalOpen}>
                    {t('common:unsubscribe')}
                  </Button>
                )}
              </Flex>
            </Form>
          )}
        </Formik>
      )}

      <ConfirmationModal
        isOpen={isUnsubscribeModalOpen}
        onClose={onUnsubscribeModalClose}
        title={t('integrations:confirmUnsubscribeWebhook')}
        subTitle=""
        confirmButtonLabel={t('common:unsubscribe')}
        confirmButtonProps={{
          bg: 'rgba(255, 83, 83, 0.1)',
          color: 'secondary.500',
          _hover: {
            bg: 'rgba(255, 83, 83, 0.2)',
          },
          _active: {
            bg: 'rgba(255, 83, 83, 0.2)',
          },
          isLoading: isUnsubscribeWebhookLoading,
          onClick: handleUnsubscribe,
        }}
      />
    </Box>
  );
};

export default Webhook;
